/**
 * Saved Searches & Search Alerts.
 *
 * Customers store a snapshot of their map filters (purpose, property type,
 * city, area, price range and distance) and optionally receive in-app
 * notifications when a newly approved listing matches. Alerts are raised by a
 * database trigger — no emails are ever sent.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Purpose, PropertyType } from "@/lib/mock-data";
import type { MapFilters } from "@/components/MapFilterDrawer";
import type { Origin } from "@/lib/geo";

export interface SavedSearch {
  id: string;
  user_id: string;
  name: string;
  purpose: Purpose | null;
  property_type: PropertyType | null;
  city: string | null;
  area: string | null;
  price_min: number | null;
  price_max: number | null;
  distance_km: number | null;
  latitude: number | null;
  longitude: number | null;
  alerts_enabled: boolean;
  last_alert_at: string | null;
  match_count: number;
  created_at: string;
  updated_at: string;
}

export type SavedSearchDraft = Pick<
  SavedSearch,
  | "name"
  | "purpose"
  | "property_type"
  | "city"
  | "area"
  | "price_min"
  | "price_max"
  | "distance_km"
  | "latitude"
  | "longitude"
  | "alerts_enabled"
>;

const TABLE = "saved_searches";

/* ------------------------------------------------------------------ *
 * Queries
 * ------------------------------------------------------------------ */

export async function listSavedSearches(userId: string): Promise<SavedSearch[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as SavedSearch[];
}

export async function createSavedSearch(
  userId: string,
  draft: SavedSearchDraft,
): Promise<SavedSearch> {
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...draft, user_id: userId } as never)
    .select("*")
    .single();
  if (error) throw error;
  return data as unknown as SavedSearch;
}

export async function renameSavedSearch(id: string, name: string): Promise<void> {
  const clean = name.trim();
  if (!clean) throw new Error("Please enter a name for this search.");
  const { error } = await supabase
    .from(TABLE)
    .update({ name: clean } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function setSavedSearchAlerts(id: string, enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .update({ alerts_enabled: enabled } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function deleteSavedSearch(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------------------ *
 * Filters <-> saved search
 * ------------------------------------------------------------------ */

export interface SearchContext {
  /** Free-text query typed in the map search bar — used as the area hint. */
  query?: string;
  /** Current map origin (GPS fix or picked city). */
  origin?: Origin | null;
  /** City typed / detected for this search. */
  city?: string | null;
}

/** Build a saveable draft from the live map filters. */
export function draftFromFilters(
  filters: MapFilters,
  name: string,
  ctx: SearchContext = {},
): SavedSearchDraft {
  const originCity = ctx.origin?.source === "city" ? ctx.origin.label : null;
  return {
    name: name.trim(),
    purpose: filters.purpose === "all" ? null : filters.purpose,
    property_type: filters.propertyType === "all" ? null : filters.propertyType,
    city: (ctx.city ?? originCity) || null,
    area: ctx.query?.trim() ? ctx.query.trim() : null,
    price_min: filters.minPrice > 0 ? filters.minPrice : null,
    price_max: filters.maxPrice > 0 ? filters.maxPrice : null,
    distance_km: filters.distanceKm > 0 ? filters.distanceKm : null,
    latitude: ctx.origin?.lat ?? null,
    longitude: ctx.origin?.lng ?? null,
    alerts_enabled: true,
  };
}

/** Turn a saved search back into map filters (merged over the defaults). */
export function filtersFromSaved(saved: SavedSearch, base: MapFilters): MapFilters {
  return {
    ...base,
    purpose: saved.purpose ?? "all",
    propertyType: saved.property_type ?? "all",
    minPrice: saved.price_min ?? 0,
    maxPrice: saved.price_max ?? 0,
    distanceKm: saved.distance_km ?? base.distanceKm,
  };
}

/** A suggested default name, e.g. "Rent · Apartment · Chennai". */
export function suggestName(filters: MapFilters, ctx: SearchContext = {}): string {
  const parts = [
    filters.purpose !== "all" ? cap(filters.purpose) : null,
    filters.propertyType !== "all" ? cap(filters.propertyType) : null,
    ctx.city ?? (ctx.origin?.source === "city" ? ctx.origin.label : null),
    ctx.query?.trim() || null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "My search";
}

function cap(v: string) {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** Human-readable chips describing what a saved search matches. */
export function describeSavedSearch(s: SavedSearch): string[] {
  const chips: string[] = [];
  chips.push(s.purpose ? cap(s.purpose) : "Any purpose");
  chips.push(s.property_type ? cap(s.property_type) : "Any type");
  if (s.city) chips.push(s.city);
  if (s.area) chips.push(s.area);
  if (s.price_min != null || s.price_max != null) {
    const lo = s.price_min != null ? `₹${inr.format(s.price_min)}` : "Any";
    const hi = s.price_max != null ? `₹${inr.format(s.price_max)}` : "Any";
    chips.push(`${lo} – ${hi}`);
  }
  if (s.distance_km != null) chips.push(`Within ${s.distance_km} km`);
  return chips;
}
